import { ActionButton, ActionStack, MessagePage } from '../components/common';
import { ProviderButtons } from './ProviderButtons';
import { MessagePatricia } from './MessagePatricia';

// The two ways Home can refuse to draw, and they are NOT the same fact.
//
// Freddi hit this on the first customer sign-in this app ever had: he was bound
// correctly and signed in perfectly well, and the page told him his access had
// ended. It had not. One message was covering a 401 and a 403 at once, and the
// words it chose belonged to neither — a 401 means the session went, a 403
// means this account is not a customer, and a person can act on each of those
// but not on a guess between them.
//
// THE RULE THAT COMES OUT OF IT: never write a message that asserts a cause the
// code has not established. "Your access has ended" is a claim about Patricia
// having revoked something. All the browser knew was that a request came back
// without a session.

export type LockedOutReason =
  /** 401 — no session, or it expired. R-IN-4: signing in again is the way back. */
  | 'signed-out'
  /**
   * 403 `no_household` — a `customer` entry with no household on it. A BROKEN
   * GRANT, not a user error and not a desk account.
   *
   * It used to read *This account is a desk account*, because a desk role
   * reaching `/home` was the only 403 anyone had seen. That cannot happen any
   * more: the role decides where a signed-in account lands before a page
   * draws (`customer/landing.ts`, Freddi 2026-09-14). What is left is the
   * grant itself being wrong, and the page says so without guessing why.
   */
  | 'no-household'
  /**
   * NEITHER OF THE ABOVE — a 500, a dropped connection, a timeout. It is not a
   * refusal at all and must not wear one's words.
   *
   * This page states the rule and then broke it one branch over: any read
   * failure that was not 401 or 403 fell through to *You are signed out*,
   * offering a sign-in to somebody whose session was perfectly fine, with no
   * way back but a reload (issue 19, 2026-09-15). All the browser knows here
   * is that it could not get an answer, so that is what it says — and it
   * offers the one action that can help.
   */
  | 'unreachable';

export function CustomerLockedOut({ reason, onRetry }: { reason: LockedOutReason; onRetry?: () => void }) {
  if (reason === 'unreachable') {
    return (
      <MessagePage
        title="We can’t reach your stays"
        lead="Something went wrong at our end, not yours — you are still signed in. Try again in a moment."
        actions={
          <ActionStack>
            <ActionButton tone="primary" leaves onClick={onRetry}>Try again</ActionButton>
            <MessagePatricia text="the app won’t load my stays — " />
          </ActionStack>
        }
      />
    );
  }

  if (reason === 'signed-out') {
    return (
      <MessagePage
        title="You are signed out"
        lead="Sign in with the same account you registered with and you will land straight back on your stays."
        // THE SAME BLOCK THE SIGN-IN PAGE USES, not two hand-rolled anchors.
        // This page is a sign-in offer; it should not be able to end up offering
        // a different set of providers, or the same ones at a different size.
        actions={<ProviderButtons />}
        foot="Signed in and still seeing this? Message Patricia — she can send you a fresh link."
      />
    );
  }

  return (
    <MessagePage
      title="We can’t find your household"
      lead="You are signed in, but this account is not on a household yet — so there are no stays to show. Patricia can put that right in a minute."
      actions={
        <ActionStack>
          <MessagePatricia text="I’m signed in but my household is missing — " />
        </ActionStack>
      }
    />
  );
}
